import React from 'react';
import PageLayout from '../../components/layout/PageLayout';
import CaseStudyTemplate from '../../components/shared/CaseStudyTemplate';

const BELEngineeringPage = () => (
  <PageLayout>
    <CaseStudyTemplate
      seoTitle="BEL Engineering Case Study | Kalobrate"
      seoDescription="See how Kalobrate delivered practical workplace learning across shifts and roles at BEL Engineering, covering sleep, stress, resilience and men's mental health."
      canonical="/results/bel-engineering"
      client="BEL Engineering"
      headline="Making workplace learning relevant to an engineering workforce"
      sector="Manufacturing and engineering"
      typeOfWork="Workforce learning"
      intro={[
        'BEL Engineering wanted workplace wellbeing activity that employees would find useful, practical and relevant to the pressures of engineering work.',
        'The sessions needed to reach people across different roles and shift patterns without becoming overly clinical or disconnected from everyday work.',
      ]}
      sections={[
        {
          heading: 'The situation',
          content: [
            'Topics such as sleep, stress, resilience and men\'s mental health can affect safety, concentration, attendance and working relationships.',
            'They can also be difficult to discuss in a workforce where employees may be sceptical of traditional wellbeing activity.',
            'BEL Engineering needed sessions that:',
            { type: 'list', items: [
              'Respected the knowledge and experience of employees',
              'Used clear and practical language',
              'Worked for engineers, managers and other colleagues',
              'Could be delivered around operational demands and shift patterns',
              'Gave people something useful to take away',
            ]},
          ],
        },
        {
          heading: 'What Kalobrate did',
          content: [
            'Kalobrate delivered 20 workplace sessions across five themes, including:',
            { type: 'list', items: [
              'Sleep',
              'Stress',
              'Resilience',
              'Men\'s mental health',
              'Practical approaches to workplace wellbeing',
            ]},
            'The content was informed by research but delivered in a way that encouraged participation without putting employees under pressure to disclose personal experiences.',
            'Sessions were arranged to reach employees across different shift patterns and levels of the organisation.',
          ],
        },
        {
          heading: 'What happened',
          content: [
            'The programme recorded:',
            { type: 'stats', items: [
              { value: '20', label: 'Workshops' },
              { value: '5', label: 'Themes' },
              { value: '239', label: 'Attendances' },
              { value: '#1', label: 'Sleep (most attended)' },
            ]},
            'Engagement came from engineers and managers. Positive changes in behaviour were reported by the client.',
          ],
        },
        {
          heading: 'What the work showed',
          content: [
            'Workplace learning is more likely to land when it reflects the people in the room.',
            'The sessions combined evidence with examples, language and practical actions that employees could relate to their own working lives.',
            'The programme also showed the value of making learning accessible across shifts rather than relying on a single event or office-based format.',
          ],
        },
      ]}
      quotes={[
        { text: "Ollie's practical and impactful sessions have resulted in noticeable positive changes in behaviours within our teams. His engaging delivery makes it easy for colleagues at all levels to actively participate and take something meaningful from each session.", name: 'Claire Hopkins', role: 'HR Manager, BEL Engineering' },
      ]}
      mainLesson={[
        'Employees do not need to be enthusiastic about wellbeing language to take something useful from a session.',
        'The content has to respect their experience, reflect their work and give them a reason to pay attention.',
      ]}
      cta={{
        heading: 'Need to engage an operational workforce in a difficult subject?',
        copy: 'Kalobrate delivers practical workplace learning designed around the people, pressures and working patterns of the organisation.',
        button: 'Talk to Ollie',
      }}
    />
  </PageLayout>
);

export default BELEngineeringPage;
